import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Inbox, ChevronDown, Trash2, MailOpen, MailCheck } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const displayName = (l) => {
  const structured = `${l.first_name || ""} ${l.last_name || ""}`.trim();
  return structured || l.name || "Unknown";
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const DETAIL_LABELS = { service: "Requested service", date: "Preferred date", time: "Preferred time" };

const Chip = ({ children, className, testId }) => (
  <span data-testid={testId} className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide border ${className}`}>
    {children}
  </span>
);

function DetailField({ label, value, testId }) {
  if (!value) return null;
  return (
    <div data-testid={testId} className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">{label}</div>
      <div className="text-sm font-semibold break-words whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function LeadRow({ lead, expanded, onToggle, onReplied, onDelete }) {
  const name = displayName(lead);
  return (
    <div className={`nb-card overflow-hidden ${lead.read ? "bg-white" : "bg-[#FDF9E8]"}`} data-testid={`lead-row-${lead.message_id}`}>
      <div className="flex items-center gap-2 pr-3">
        <button
          onClick={onToggle}
          data-testid={`lead-toggle-${lead.message_id}`}
          aria-expanded={expanded}
          className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3.5 text-left"
        >
          {!lead.read && <span className="w-2 h-2 rounded-full bg-[#3B82F6] flex-shrink-0" data-testid={`lead-unread-dot-${lead.message_id}`} aria-label="Unread" />}
          <span className={`truncate ${lead.read ? "font-semibold" : "font-extrabold"}`} data-testid={`lead-name-${lead.message_id}`}>{name}</span>
          <Chip testId={`lead-source-${lead.message_id}`} className={lead.source === "consultation" ? "bg-[#EDE9FE] border-[#C4B5FD]" : "bg-[#E0F2FE] border-[#7DD3FC]"}>
            {lead.source === "consultation" ? "Consultation" : "Contact"}
          </Chip>
          <Chip testId={`lead-read-state-${lead.message_id}`} className={lead.read ? "bg-gray-100 border-gray-300 text-gray-500" : "bg-[#DBEAFE] border-[#93C5FD] text-[#1D4ED8]"}>
            {lead.read ? "Read" : "New"}
          </Chip>
          {lead.replied && (
            <Chip testId={`lead-replied-state-${lead.message_id}`} className="bg-[#DCFCE7] border-[#86EFAC] text-[#15803D]">Replied</Chip>
          )}
          <span className="ml-auto flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-500 font-semibold hidden sm:inline" data-testid={`lead-date-${lead.message_id}`}>{fmtDate(lead.created_at)}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </span>
        </button>
        <button
          onClick={onDelete}
          data-testid={`lead-delete-${lead.message_id}`}
          aria-label={`Delete ${name}`}
          className="p-2 rounded-lg text-red-600 hover:bg-red-50 flex-shrink-0"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="border-t-2 border-black px-4 py-4 bg-white" data-testid={`lead-details-${lead.message_id}`}>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            <DetailField label="Full name" value={name} testId={`lead-detail-name-${lead.message_id}`} />
            <DetailField label="Email" value={lead.email} testId={`lead-detail-email-${lead.message_id}`} />
            <DetailField label="Phone" value={lead.phone} testId={`lead-detail-phone-${lead.message_id}`} />
            <DetailField label="Source" value={lead.source === "consultation" ? "Consultation request" : "Contact form"} testId={`lead-detail-source-${lead.message_id}`} />
            <DetailField label="Received" value={fmtDate(lead.created_at)} testId={`lead-detail-received-${lead.message_id}`} />
            {["service", "date", "time"].map((k) => (
              <DetailField key={k} label={DETAIL_LABELS[k]} value={lead.details?.[k]} testId={`lead-detail-${k}-${lead.message_id}`} />
            ))}
            {Object.entries(lead.details || {})
              .filter(([k]) => !DETAIL_LABELS[k])
              .map(([k, v]) => (
                <DetailField key={k} label={k.replace(/_/g, " ")} value={v} testId={`lead-detail-${k}-${lead.message_id}`} />
              ))}
          </div>
          {lead.message && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500 mb-1">Message</div>
              <div className="text-sm whitespace-pre-wrap break-words bg-gray-50 rounded-xl border border-gray-200 p-3" data-testid={`lead-message-${lead.message_id}`}>
                {lead.message}
              </div>
            </div>
          )}
          <div className="mt-4">
            <button
              onClick={onReplied}
              data-testid={`lead-replied-toggle-${lead.message_id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-extrabold border-2 border-black nb-press ${
                lead.replied ? "bg-[#DCFCE7]" : "bg-white"
              }`}
            >
              {lead.replied ? <MailCheck className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
              {lead.replied ? "Replied ✓ — mark as not replied" : "Mark as replied"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ContactsTab({ onUnreadChange }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!loading && onUnreadChange) onUnreadChange(leads.filter((l) => !l.read).length);
  }, [leads, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/leads");
        if (alive) setLeads(data.leads || []);
      } catch { if (alive) toast.error("Couldn't load contacts"); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const patchLead = async (message_id, updates) => {
    setLeads((ls) => ls.map((l) => (l.message_id === message_id ? { ...l, ...updates } : l)));
    try {
      await api.patch(`/leads/${message_id}`, updates);
    } catch {
      toast.error("Couldn't update contact");
    }
  };

  const toggleExpand = (lead) => {
    const opening = expandedId !== lead.message_id;
    setExpandedId(opening ? lead.message_id : null);
    if (opening && !lead.read) patchLead(lead.message_id, { read: true });
  };

  const confirmDelete = async () => {
    const lead = deleteTarget;
    setDeleteTarget(null);
    if (!lead) return;
    try {
      await api.delete(`/leads/${lead.message_id}`);
      setLeads((ls) => ls.filter((l) => l.message_id !== lead.message_id));
      toast.success("Contact deleted");
    } catch {
      toast.error("Couldn't delete contact");
    }
  };

  return (
    <div data-testid="contacts-tab">
      <div className="nb-card p-5 mb-6">
        <div className="font-display text-2xl font-extrabold flex items-center gap-2">
          <Inbox className="w-6 h-6" /> Contacts
        </div>
        <div className="text-sm text-gray-600 mt-1">
          Contact and consultation requests from your website, newest first.
        </div>
      </div>

      {loading ? (
        <div className="nb-card-flat p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : leads.length === 0 ? (
        <div className="nb-card-flat p-10 text-center" data-testid="contacts-empty-state">
          <Inbox className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <div className="font-extrabold text-lg">No contacts yet</div>
          <div className="text-sm text-gray-600 mt-1">
            Contact and consultation requests from your website will appear here.
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="contacts-list">
          {leads.map((lead) => (
            <LeadRow
              key={lead.message_id}
              lead={lead}
              expanded={expandedId === lead.message_id}
              onToggle={() => toggleExpand(lead)}
              onReplied={() => patchLead(lead.message_id, { replied: !lead.replied })}
              onDelete={() => setDeleteTarget(lead)}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="lead-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this contact?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>You're about to permanently delete the {deleteTarget.source === "consultation" ? "consultation request" : "contact message"} from <b>{displayName(deleteTarget)}</b>{deleteTarget.email ? ` (${deleteTarget.email})` : ""}. This can't be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="lead-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
